import { useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Fragment } from "react/jsx-runtime";

import CheckIcon from "src/core/assets/images/icons/check.svg?react";
import CheckCircleIcon from "src/core/assets/images/icons/checkCircle.svg?react";
import ChevronDownIcon from "src/core/assets/images/icons/chevronDown.svg?react";
import ChevronUpIcon from "src/core/assets/images/icons/chevronUp.svg?react";
import { Button } from "src/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "src/core/components/ui/dialog";
import { CompareCustomerGraphWithExternalDataQuery } from "src/core/gql/graphql";
import { useViewModification } from "src/core/hooks/folder/useViewModification";
import { cn } from "src/core/lib/utils";

type ViewModificationModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  compareData: CompareCustomerGraphWithExternalDataQuery["compareCustomerGraphWithExternalData"];
  onFolderAccess: () => void;
};

const profilDeletedAndAddedComponents = {
  red: <span className="text-red-medium mx-[1px] inline" />,
  blue: <span className="text-blue-info mx-[1px] inline" />,
};

export function ProfilDeletedLabel({
  beneficiaryOwner,
  legalRepresentative,
}: {
  beneficiaryOwner: boolean;
  legalRepresentative: boolean;
}) {
  const type = [legalRepresentative && "RL", beneficiaryOwner && "BE"].filter(Boolean).join(", ");

  return (
    <Trans
      i18nKey="modal.viewModifications.profilDeleted"
      values={{ type }}
      components={profilDeletedAndAddedComponents}
    />
  );
}

export function ProfilAddedLabel({ type }: { type: string }) {
  return (
    <Trans
      i18nKey="modal.viewModifications.profilAdded"
      values={{ type }}
      components={profilDeletedAndAddedComponents}
    />
  );
}

export function CheckIconSelected({ selected }: { selected: boolean }) {
  if (selected) {
    return <CheckIcon className="w-20 h-20 shrink-0 text-blue-completed" />;
  } else {
    return <CheckCircleIcon className="w-20 h-20 shrink-0 text-muted-foreground" />;
  }
}

export const ViewModificationModal = ({
  open,
  setOpen,
  compareData,
  onFolderAccess,
}: ViewModificationModalProps) => {
  const { t } = useTranslation();
  const {
    countDifferencesFound,
    formatData,
    getAddedProfileTypeLabel,
    expandedIndices,
    toggleExpanded,
    getSelectedValueSide,
    setSelectedValueSide,
    getSelectedProfileAction,
    setSelectedProfileAction,
    resetSelectedValueSides,
    handleSubmit,
    isLoading,
  } = useViewModification(compareData, onFolderAccess);

  const prevOpenRef = useRef(open);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    if (!wasOpen && open) {
      resetSelectedValueSides();
    }
    prevOpenRef.current = open;
  }, [open, resetSelectedValueSides]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="min-w-[610px] max-h-[calc(100vh-80px)] overflow-y-auto"
        hideCloseButton
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="mb-15">
          <DialogTitle>
            {t("modal.viewModifications.title", { count: countDifferencesFound })}
          </DialogTitle>
          <DialogDescription className="sr-only"></DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto">
          <div className="font-medium">
            {t("modal.viewModifications.viewModificationsDescription")}
          </div>
          <div className="font-light">
            {t("modal.viewModifications.viewModificationsDescription2")}
          </div>
          <div className="flex flex-col gap-y-10 mt-10">
            {formatData()
              .filter((stakeholder) => stakeholder.count > 0)
              .map((stakeholder, index) => {
                const isExpanded = expandedIndices.has(index);
                const stakeholderKey =
                  stakeholder.stakeholder.folderStakeholder?.entity.id ?? String(index);
                const diffs = stakeholder.diffInputValues ?? [];
                const selectedProfileAction = getSelectedProfileAction(
                  stakeholderKey,
                  stakeholder.modificationType
                );

                return (
                  <div key={index} className="small-card flex flex-col items-start font-light">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(index)}
                      className="flex flex-row items-center justify-between w-full text-left cursor-pointer"
                    >
                      <div>{stakeholder.name}</div>
                      <div className="flex flex-row items-center gap-x-20">
                        <div>({stakeholder.count})</div>
                        {isExpanded ? (
                          <ChevronUpIcon className="text-foreground" />
                        ) : (
                          <ChevronDownIcon className="text-foreground" />
                        )}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="flex flex-col gap-y-10 w-full">
                        {stakeholder.modificationType === "ADDED" ? (
                          <>
                            <div className="mt-10 font-medium text-xs">
                              <ProfilAddedLabel
                                type={getAddedProfileTypeLabel(
                                  (
                                    stakeholder.stakeholder.externalData?.[0] as
                                      | { rolesToAdd?: { typeCode?: string | null }[] }
                                      | undefined
                                  )?.rolesToAdd
                                )}
                              />
                            </div>
                            <div className="mt-10 grid grid-cols-2 gap-x-10 gap-y-5 w-full">
                              <div
                                className={cn(
                                  "form-input value cursor-pointer",
                                  selectedProfileAction === "remove" ? "value-selected" : ""
                                )}
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedProfileAction(stakeholderKey, "remove")}
                              >
                                <span className="flex-1 whitespace-normal break-words">
                                  {t("modal.viewModifications.removeProfile")}
                                </span>
                                <CheckIconSelected selected={selectedProfileAction === "remove"} />
                              </div>
                              <div
                                className={cn(
                                  "form-input value cursor-pointer",
                                  selectedProfileAction === "add" ? "value-selected" : ""
                                )}
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedProfileAction(stakeholderKey, "add")}
                              >
                                <span className="flex-1 whitespace-normal break-words">
                                  {t("modal.viewModifications.addProfile")}
                                </span>
                                <CheckIconSelected selected={selectedProfileAction === "add"} />
                              </div>
                            </div>
                          </>
                        ) : stakeholder.modificationType === "DELETED" ? (
                          <>
                            <div className="mt-10 font-medium text-xs">
                              <ProfilDeletedLabel
                                beneficiaryOwner={
                                  stakeholder.stakeholder.folderStakeholder?.beneficiaryOwner ??
                                  false
                                }
                                legalRepresentative={
                                  stakeholder.stakeholder.folderStakeholder
                                    ?.isLegalRepresentative ?? false
                                }
                              />
                            </div>
                            <div className="mt-10 grid grid-cols-2 gap-x-10 gap-y-5 w-full">
                              <div
                                className={cn(
                                  "form-input value cursor-pointer",
                                  selectedProfileAction === "keep" ? "value-selected" : ""
                                )}
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedProfileAction(stakeholderKey, "keep")}
                              >
                                <span className="flex-1 whitespace-normal break-words te">
                                  {t("modal.viewModifications.keepProfile")}
                                </span>
                                <CheckIconSelected selected={selectedProfileAction === "keep"} />
                              </div>
                              <div
                                className={cn(
                                  "form-input value cursor-pointer",
                                  selectedProfileAction === "remove" ? "value-selected" : ""
                                )}
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedProfileAction(stakeholderKey, "remove")}
                              >
                                <span className="flex-1 whitespace-normal break-words">
                                  {t("modal.viewModifications.removeProfile")}
                                </span>
                                <CheckIconSelected selected={selectedProfileAction === "remove"} />
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="mt-10 grid grid-cols-2 gap-x-10 gap-y-5 w-full">
                            <div className="text-muted-foreground">
                              {t("modal.viewModifications.oldValue")}
                            </div>
                            <div>{t("modal.viewModifications.newValue")}</div>
                            {diffs.map((diff) => {
                              const selectedSide = getSelectedValueSide(
                                stakeholderKey,
                                diff.fieldCode
                              );
                              return (
                                <Fragment key={diff.fieldCode}>
                                  <div
                                    className={cn(
                                      "form-input value",
                                      selectedSide === "old"
                                        ? "value-selected"
                                        : "text-muted-foreground"
                                    )}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() =>
                                      setSelectedValueSide(stakeholderKey, diff.fieldCode, "old")
                                    }
                                  >
                                    <span className="flex-1 whitespace-normal break-words">
                                      {diff.oldValue ?? "—"}
                                    </span>
                                    <CheckIconSelected selected={selectedSide === "old"} />
                                  </div>
                                  <div
                                    className={cn(
                                      "form-input value",
                                      selectedSide === "new" ? "value-selected" : ""
                                    )}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() =>
                                      setSelectedValueSide(stakeholderKey, diff.fieldCode, "new")
                                    }
                                  >
                                    <span className="flex-1 whitespace-normal break-words">
                                      {diff.newValue ?? "—"}
                                    </span>
                                    <CheckIconSelected selected={selectedSide === "new"} />
                                  </div>
                                </Fragment>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
        <Button className="w-full mt-20" onClick={handleSubmit} loading={isLoading}>
          {t("modal.viewModifications.submit")}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
